// The agency's public brand for pages that are not the agency's own app (X3-14). A public page with a link token reads it
// from its response (`brand`, the same shape as GET /s/<slug>/_brand); the client portal reads GET /s/<slug>/_brand with
// the signed-in workspace's slug. Both become a BrandMark for the BrandProvider in @offsilo/ui/brand.

import type { BrandMark } from "@offsilo/ui/brand";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { serverMode } from "./actions";
import { useSignInWorkspace } from "./hooks";

/** The brand as the server publishes it. Null or missing when the workspace has none. */
export type PublicBrandPayload =
  | {
      name?: string;
      logo?: string | null;
      favicon?: string | null;
      /** The app icon's public address: a Website media file or the typed https address (X11-420b). */
      appIcon?: string | null;
      font?: string;
      colour?: string | null;
      whiteLabel?: boolean;
    }
  | null
  | undefined;

/** The mark of a published brand, or null without one. */
export function markOf(brand: PublicBrandPayload): BrandMark | null {
  if (!brand) return null;
  return {
    name: brand.name ?? "",
    logo: brand.logo ?? null,
    colour: brand.colour ?? null,
    font: brand.font ?? null,
    favicon: brand.favicon ?? null,
  };
}

/** The portal's mark, read from the public brand of the signed-in workspace. Null in sample mode, or while the brand loads (the
 *  portal then shows the platform's accent until it arrives). Call it only where the portal is shown. */
export function usePortalBrandMark(): BrandMark | null {
  const slug = useSignInWorkspace().data?.slug;
  const brand = useQuery({
    queryKey: ["public-brand", slug],
    queryFn: async (): Promise<PublicBrandPayload> => {
      const res = await fetch(`/s/${encodeURIComponent(slug ?? "")}/_brand`);
      return res.ok ? ((await res.json()) as PublicBrandPayload) : null;
    },
    enabled: serverMode && !!slug,
    staleTime: 5 * 60_000,
  });
  const data = brand.data;
  return useMemo(() => markOf(data), [data]);
}
